import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component-footer';
import useCampaignForecastConfidence from '../../../hooks/stats/useCampaignForecastConfidence';
import {
    I18N,
    formatConfidence,
    formatCount,
    formatRequestInterval,
    getRegularityLabel,
    getRegularityTooltip,
    getRequestsCoverageNotice,
    sortRowsNullLast,
    toUtcRange,
} from '../../../utils/forecastConfidence';
import ForecastConfidenceScreenDetail from './ForecastConfidenceScreenDetail';

const Kpi = ({ label, value, hint, tone = 'text-gray-900', testId }) => (
    <div
        className="flex flex-col p-3 rounded-lg bg-gray-50"
        title={hint}
        data-testid={testId}
    >
        <span className="font-light text-xs uppercase text-gray-600">
            {label}
        </span>
        <span className={`text-xl font-semibold ${tone}`}>{value}</span>
    </div>
);

const ConfidenceCell = ({ value }) => {
    const hasValue = value !== null && value !== undefined;
    const width = hasValue ? Math.max(0, Math.min(1, value)) * 100 : 0;
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="h-1.5 w-16 rounded bg-gray-200 overflow-hidden">
                <div
                    className={`h-full ${
                        hasValue && value < 0.7 ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${width}%` }}
                />
            </div>
            <span className="font-mono">{formatConfidence(value)}</span>
        </div>
    );
};

const buildColumns = (t) => [
    {
        id: 'screen',
        name: t(`${I18N}.columns.screen`),
        selector: (row) => row.screen_name || row.screen_id,
        sortable: true,
        grow: 1,
        minWidth: '220px',
        // The table grows with its content: cap it so long names truncate
        // and the eight columns fit the dashboard module.
        maxWidth: '300px',
        cell: (row) => (
            // allowRowEvents: custom cells only reach onRowClicked with it.
            // One line per row: long names are truncated, full name on hover.
            <div
                className="flex items-center gap-2 py-1 min-w-0 w-full"
                data-tag="allowRowEvents"
            >
                {row.needs_review && (
                    <span
                        className="flex-shrink-0 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold"
                        title={t(`${I18N}.review-hint`)}
                        data-testid="forecast-confidence-review"
                        data-tag="allowRowEvents"
                    >
                        {t(`${I18N}.review`)}
                    </span>
                )}
                <span
                    className="truncate text-blue-600 underline cursor-pointer"
                    title={row.screen_name || row.screen_id}
                    data-tag="allowRowEvents"
                >
                    {row.screen_name || row.screen_id}
                </span>
            </div>
        ),
    },
    {
        id: 'confidence',
        name: t(`${I18N}.columns.confidence`),
        selector: (row) => row.confidence,
        sortable: true,
        width: '140px',
        cell: (row) => <ConfidenceCell value={row.confidence} />,
    },
    {
        id: 'forecast',
        name: t(`${I18N}.columns.forecast`),
        selector: (row) => row.forecast_impressions,
        sortable: true,
        right: true,
        width: '110px',
        format: (row) => formatCount(row.forecast_impressions),
    },
    {
        id: 'delivered',
        name: t(`${I18N}.columns.delivered`),
        selector: (row) => row.delivered_impressions,
        sortable: true,
        right: true,
        width: '110px',
        format: (row) => formatCount(row.delivered_impressions),
    },
    {
        id: 'requests',
        name: (
            <span title={t(`${I18N}.requests-hint`)}>
                {t(`${I18N}.columns.requests`)}
            </span>
        ),
        selector: (row) => row.requests,
        sortable: true,
        right: true,
        width: '110px',
        format: (row) => formatCount(row.requests),
    },
    {
        id: 'regularity',
        name: t(`${I18N}.columns.regularity`),
        selector: (row) => row.regularity?.presence_ratio,
        sortable: true,
        width: '170px',
        cell: (row) => (
            <span title={getRegularityTooltip(row.regularity, t)}>
                {getRegularityLabel(row.regularity, t)}
            </span>
        ),
    },
    {
        id: 'interval',
        name: (
            <span title={t(`${I18N}.interval-hint`)}>
                {t(`${I18N}.columns.interval`)}
            </span>
        ),
        selector: (row) => row.avg_request_interval_sec,
        sortable: true,
        right: true,
        width: '110px',
        format: (row) => formatRequestInterval(row.avg_request_interval_sec, t),
    },
    {
        id: 'missed-hours',
        name: t(`${I18N}.columns.missed-hours`),
        selector: (row) => row.missed_opportunity_hours,
        sortable: true,
        right: true,
        width: '110px',
        cell: (row) => (
            <span
                className={
                    row.missed_opportunity_hours > 0
                        ? 'text-red-600 font-semibold'
                        : ''
                }
                data-testid="forecast-confidence-missed"
            >
                {formatCount(row.missed_opportunity_hours)}
            </span>
        ),
    },
];

/**
 * "Confianza del pronóstico" (admin only): per screen, how much of the
 * forecast plan was bought in the dashboard period, how many requests the
 * screen received and how regular they were. Clicking a screen opens its
 * hourly detail.
 */
function ForecastConfidencePanel({ campaign, currentPeriod, requestConfig }) {
    const { t } = useTranslation();
    const periodFrom = currentPeriod?.[0];
    const periodTo = currentPeriod?.[1];
    const range = useMemo(
        () => toUtcRange([periodFrom, periodTo]),
        [periodFrom, periodTo]
    );
    const rangeKey = range ? `${range.from}|${range.to}` : '';
    // The selection belongs to a period: a new period closes the detail.
    const [selection, setSelection] = useState({ rangeKey: '', screen: null });
    const selectedScreen =
        selection.rangeKey === rangeKey ? selection.screen : null;

    const { data, error, isLoading } = useCampaignForecastConfidence({
        campaignId: campaign?.id,
        from: range?.from,
        to: range?.to,
        requestConfig,
    });

    const columns = useMemo(() => buildColumns(t), [t]);
    const screens = data?.screens || [];
    const totals = data?.totals;
    const notice = data ? getRequestsCoverageNotice(data, t) : null;

    const toggleScreen = (row) =>
        setSelection(
            selectedScreen?.screen_id === row.screen_id
                ? { rangeKey, screen: null }
                : { rangeKey, screen: row }
        );

    const conditionalRowStyles = [
        {
            when: (row) => row.screen_id === selectedScreen?.screen_id,
            style: { backgroundColor: '#EFF6FF' },
        },
    ];

    let body;
    if (isLoading) {
        body = (
            <p className="text-sm text-gray-500 mt-2" aria-busy="true">
                {t(`${I18N}.loading`)}
            </p>
        );
    } else if (error) {
        body = (
            <p className="text-sm text-red-500 mt-2" role="status">
                {t(`${I18N}.error`)}
            </p>
        );
    } else if (!data || screens.length === 0) {
        body = (
            <p className="text-sm text-gray-500 mt-2" role="status">
                {t(`${I18N}.empty`)}
            </p>
        );
    } else {
        body = (
            <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    <Kpi
                        label={t(`${I18N}.kpis.confidence`)}
                        hint={t(`${I18N}.kpis.confidence-hint`)}
                        value={formatConfidence(totals?.confidence)}
                        testId="forecast-confidence-kpi-confidence"
                    />
                    <Kpi
                        label={t(`${I18N}.kpis.screens-to-review`)}
                        value={formatCount(totals?.screens_needing_review)}
                        tone={
                            totals?.screens_needing_review > 0
                                ? 'text-red-600'
                                : undefined
                        }
                        testId="forecast-confidence-kpi-review"
                    />
                    <Kpi
                        label={t(`${I18N}.kpis.missed-hours`)}
                        value={formatCount(totals?.missed_opportunity_hours)}
                        tone={
                            totals?.missed_opportunity_hours > 0
                                ? 'text-red-600'
                                : undefined
                        }
                        testId="forecast-confidence-kpi-missed"
                    />
                    <Kpi
                        label={t(`${I18N}.kpis.screens`)}
                        value={formatCount(totals?.screens ?? screens.length)}
                    />
                </div>
                {notice && (
                    <p
                        className="mt-2 text-xs px-3 py-2 rounded bg-yellow-50 text-yellow-800 border border-yellow-200"
                        role="note"
                        data-testid="forecast-confidence-notice"
                    >
                        {notice}
                    </p>
                )}
                <div className="mt-2">
                    <DataTable
                        columns={columns}
                        data={screens}
                        keyField="screen_id"
                        footer={null}
                        sortFunction={sortRowsNullLast}
                        onRowClicked={toggleScreen}
                        conditionalRowStyles={conditionalRowStyles}
                        highlightOnHover
                        pointerOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="50vh"
                    />
                </div>
                {selectedScreen && (
                    <ForecastConfidenceScreenDetail
                        key={`${rangeKey}|${selectedScreen.screen_id}`}
                        campaignId={campaign?.id}
                        range={range}
                        screen={selectedScreen}
                        requestConfig={requestConfig}
                        onClose={() => setSelection({ rangeKey, screen: null })}
                    />
                )}
            </>
        );
    }

    return (
        <div
            className="module w-full my-2"
            data-testid="forecast-confidence-panel"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-light text-normal uppercase">
                    {t(`${I18N}.title`)}
                </h2>
                <span className="text-xs text-gray-500">
                    {t(`${I18N}.subtitle`)}
                </span>
            </div>
            {body}
        </div>
    );
}

export default ForecastConfidencePanel;
